import { motion } from "motion/react";
import { Globe2 } from "lucide-react";
import { PartnerUniData } from "../../Data/partnerUniData";
import MY from "../../assets/logo/country/malaysia.png";
import UK from "../../assets/logo/country/uk.png";
import SG from "../../assets/logo/country/singapore.png";
// import AU from "../../assets/logo/country/australia.png";

const partners = PartnerUniData.map((uni) => ({
  name: uni.uniName,
  country: uni.country,
  logo: uni.uniLogo,
}));

const countryColors: Record<string, string> = {
  MY: "#7C3AED",
  UK: "#06B6D4",
  // AU: "#F97316",
  SG: "#84CC16",
  // IE: "#EC4899",
};

const countryLabels: Record<string, string> = {
  MY: "Malaysia",
  UK: "United Kingdom",
  // AU: "Australia",
  SG: "Singapore",
  // IE: "Ireland",
};

const countryStats = [
  { flag: MY, country: "Malaysia", count: "150+", isImage: true },
  // { flag: AU, country: "Australia", count: "60+", isImage: true },
  { flag: UK, country: "United Kingdom", count: "40+", isImage: true },
  { flag: SG, country: "Singapore", count: "25+", isImage: true },
  // { flag: "🇮🇪", country: "Ireland & Others", count: "30+" },
];

// Duplicate for infinite scroll
const midIndex = Math.ceil(partners.length / 2);
const row1 = [...partners.slice(0, midIndex), ...partners.slice(0, midIndex)];
const row2 = [...partners.slice(midIndex), ...partners.slice(midIndex)];

function PartnerChip({
  partner,
}: {
  partner: { name: string; country: string; color: string; logo: any };
}) {
  return (
    <div className="flex items-center gap-2.5 px-5 py-3 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200 cursor-default whitespace-nowrap group">
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] flex-shrink-0 font-bold"
      >
        <img src={partner.logo} alt=""/>
      </div>
      <div className="flex flex-col leading-none">
        <span
          className="text-[13px] text-slate-800"
          style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
        >
          {partner.name}
        </span>
        <span
          className="text-[10px] mt-0.5"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            color: countryColors[partner.country] || "#94a3b8",
          }}
        >
          {countryLabels[partner.country] || partner.country}
        </span>
      </div>
    </div>
  );
}

export function PartnersSection() {
  return (
    <section className="py-24 lg:py-32 bg-[#FAFAFA] overflow-hidden relative" id="partners">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 mb-6">
            <Globe2 className="w-3.5 h-3.5 text-slate-600" />
            <span
              className="text-[12px] text-slate-600 uppercase tracking-widest"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
            >
              Our Partners
            </span>
          </div>
          <h2
            className="text-[40px] lg:text-[52px] text-slate-900 tracking-tight mb-4"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, lineHeight: 1.1 }}
          >
            Trusted by{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EC4899)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              300+
            </span>{" "}
            institutions.
          </h2>
          <p
            className="text-[16px] text-slate-500 max-w-xl mx-auto"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            From top Malaysian public universities to globally ranked overseas institutions — our
            network has you covered.
          </p>
        </motion.div>
      </div>

      {/* Country stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="max-w-7xl mx-auto px-6 lg:px-8 mb-14"
      >
        <div className="flex flex-wrap justify-center gap-4">
          {countryStats.map((stat, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200"
            >
              <div className="w-7 h-5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {stat.isImage ? (
                  <img
                    src={stat.flag}
                    alt={`${stat.country} flag`}
                    className="w-full h-full object-cover rounded-[3px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] border border-slate-200/60"
                  />
                ) : (
                  <span className="text-[22px] leading-none select-none">{stat.flag}</span>
                )}
              </div>
              <div className="flex flex-col leading-none">
                <span
                  className="text-[12px] text-slate-500"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                >
                  {stat.country}
                </span>
                <span
                  className="text-[18px] text-slate-900"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 800 }}
                >
                  {stat.count}
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Ticker Row 1 — scroll left */}
      <div className="relative mb-4">
        <div
          className="flex gap-3 animate-marquee-left"
          style={{ width: "max-content" }}
        >
          {row1.map((partner, i) => (
            <PartnerChip key={`r1-${i}`} partner={partner} />
          ))}
        </div>
      </div>

      {/* Ticker Row 2 — scroll right */}
      <div className="relative">
        <div
          className="flex gap-3 animate-marquee-right"
          style={{ width: "max-content" }}
        >
          {row2.map((partner, i) => (
            <PartnerChip key={`r2-${i}`} partner={partner} />
          ))}
        </div>
      </div>

      {/* Edge fades */}
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FAFAFA] to-transparent pointer-events-none z-10" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FAFAFA] to-transparent pointer-events-none z-10" />

      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .animate-marquee-left {
          animation: marquee-left 30s linear infinite;
        }
        .animate-marquee-right {
          animation: marquee-right 30s linear infinite;
        }
      `}</style>
    </section>
  );
}
